import React, { FC, useEffect, useMemo, useState } from "react";
import { Image, View } from "@lookiero/aurora";
import { Text } from "@lookiero/sty-psp-ui";
import { VariantWithColorAndImage } from "../../../../../../domain/projections/VariantWithColorAndImage";
import { useGetVariantColors } from "../../../../behaviors/useGetVariantColors";
import { TouchableWithoutFeedback } from "../../../../components/touchableWithoutFeedback/TouchableWithoutFeedback";
import { cdnImageUrl } from "../../../../shared/util/cdnImageUrl";
import { style as expandableColorPickerStyle, imageWidth } from "./ExpandableColorPicker.styles";

const NUM_OF_ITEMS = 5;

interface ExpandableColorPickerProps {
  readonly variantId: string;
  readonly color: string;
  readonly onPressColor: (color: VariantWithColorAndImage) => void;
}

const ExpandableColorPicker: FC<ExpandableColorPickerProps> = ({ color, variantId, onPressColor }) => {
  const { variantColors } = useGetVariantColors(variantId);
  const [colorVariantsToRender, setColorVariantsToRender] = useState<VariantWithColorAndImage[]>([]);

  const style = useMemo(() => expandableColorPickerStyle(), []);

  const colorVariants = useMemo(() => {
    if (!variantColors) {
      return [];
    }
    const variantsPerColor = variantColors.reduce(
      (acc, colorVariant) => {
        const colorId = colorVariant.color.id;
        if (!acc[colorId] && colorVariant.isSizeRecommended && colorVariant.isSizeAvailable) {
          acc[colorId] = colorVariant;
        }
        return acc;
      },
      {} as Record<string, VariantWithColorAndImage>,
    );

    return Object.values(variantsPerColor);
  }, [variantColors]);

  const showMoreColors = colorVariants.length > colorVariantsToRender?.length;

  const selectedColor = useMemo(() => {
    if (color && colorVariants.length > 0) {
      const colorVariant = colorVariants.find((variant) => variant.color.name === color);
      if (colorVariant) {
        return colorVariant.id;
      }
    }
    return "";
  }, [colorVariants, color]);

  useEffect(() => {
    const reducedColorVariants = colorVariants.slice(0, NUM_OF_ITEMS);
    setColorVariantsToRender(reducedColorVariants);
  }, [colorVariants]);

  const setAllColorToRender = () => {
    setColorVariantsToRender([...colorVariants]);
  };

  return colorVariantsToRender?.length ? (
    <View style={style.wrapper} testID="color-picker">
      {colorVariantsToRender.map((colorVariant) => {
        return (
          <TouchableWithoutFeedback
            key={colorVariant.id}
            style={[style.oneSixth, style.imageWrapper]}
            onPress={() => onPressColor(colorVariant)}
          >
            <Image
              resizeMode="cover"
              src={cdnImageUrl({ imageUrl: colorVariant.url, width: imageWidth })}
              style={[style.image, colorVariant.id === selectedColor ? style.selectedImage : null]}
            />
          </TouchableWithoutFeedback>
        );
      })}
      {showMoreColors ? (
        <TouchableWithoutFeedback
          key="more-colors"
          style={[style.oneSixth, style.moreColorsButton]}
          onPress={setAllColorToRender}
        >
          <View style={style.moreColors}>
            <Text level={1} style={style.moreColorsText} variant="detail">
              + {colorVariants.length - NUM_OF_ITEMS}
            </Text>
          </View>
        </TouchableWithoutFeedback>
      ) : null}
    </View>
  ) : null;
};

export { ExpandableColorPicker };
